import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DateTime } from 'luxon';
import { DashboardService } from '../dashboard/dashboard.service';
import { MatDialog } from '@angular/material/dialog';
import { HierarchyModalComponent } from '../universal-search/hierarchy-modal/hierarchy-modal.component';
import { EnvironmentService } from '../services/environment.service';
import { Platform } from '@angular/cdk/platform';
import { saveAs } from '@progress/kendo-file-saver';
import { interval, forkJoin } from 'rxjs';
import { mergeMap, takeWhile, map } from 'rxjs/operators';
import { SessionStorageService } from '../services/session-storage.service';
import { DateHandlingService } from '../services/date-handling.service';
import { AdminService } from '../admin/admin.service';
import { RbGridComponent } from '../shared-components/rb-grid/rb-grid.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarnDialogComponent } from '../warn-dialog/warn-dialog.component';
import { dashboardLinks } from '../lib/toolbar-links';

@Component({
  selector: 'app-case-explorer',
  templateUrl: './case-explorer.component.html',
  styleUrls: ['./case-explorer.component.scss'],
  standalone: false,
})
export class CaseExplorerComponent implements OnInit, OnDestroy {
  private adminService = inject(AdminService);
  private ss = inject(SessionStorageService);
  private dateSvc = inject(DateHandlingService);
  private dbs = inject(DashboardService);
  dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private envSvc = inject(EnvironmentService);
  private pltfrm = inject(Platform);
  private snacky = inject(MatSnackBar);

  @ViewChild(RbGridComponent) rbGrid: RbGridComponent;

  private environment;
  unitsManaged = this.ss.get('globals')
    ? this.ss.get('globals').user.managerUnits
    : {};
  unit =
    this.unitsManaged && this.unitsManaged.length > 0
      ? this.unitsManaged[0].id
      : null;
  app = this.ss.get('currentApp');
  startDate = new FormControl(
    DateTime.now().minus({ days: 7 }).startOf('day').toUTC().toISO()
  );
  endDate = new FormControl(DateTime.now().endOf('day').toUTC().toISO());
  solutions = [];
  solution = 'pp';
  hidden = [];
  profile;
  flatOrGrouped = 'GROUP';
  limit30 = true;

  scoreClass;
  rights = [];
  globals = {};
  gridData = {};
  private baseUrl: string;
  iteration = 'CURRENT';
  selectedUser = null;
  selectedUnit = null;
  selectedRepCode = null;
  gridConfig = {
    title: 'Case Explorer',
    gridName: 'CaseExplorer',
    manualSearch: true,
    searchButtonText: 'Update Search',
    showGridFilters: true,
    showFilterManager: true,
    autoReports: true,
    gridActions: [
      {
        icon: 'history',
        color: 'warn',
        text: 'View Case History',
        action: this.viewHistory,
      },
      {
        icon: 'work',
        color: 'primary',
        text: 'Open Case',
        action: this.viewCase,
      },
    ],
    previewActionText: 'Open Case',
    gridType: 'caseExplorer',
    conditionalSearch: true,
    groupByOptions: [
      { display: 'Case', value: 'group' },
      { display: 'Product/Category', value: 'products' },
      { display: 'Full Review', value: 'review' },
    ],
  };
  fileLoading = false;

  navCollapsed = false;
  downloadLimit: string = '100,000';
  dashboardLinks = dashboardLinks;

  ngOnInit(): void {
    this.navCollapsed = this.ss.get('utilityToolbarCollapsed');
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;

    this.rights = this.ss.get('rights') ? this.ss.get('rights') : [];
    this.limit30 = this.rights.includes('limit30Days');

    this.route.paramMap.subscribe(params => {
      if (params['params']['user']) {
        this.selectedUser = {
          name: params['params']['user'],
          id: params['params']['user'],
        };
      }
      if (params['params']['scoreClass']) {
        this.scoreClass = params['params']['scoreClass'];
      }
    });
  }

  ngOnDestroy(): void {
    this.ss.remove('CaseExplorerFilters');
    this.ss.remove('CaseExplorerSort');
    this.ss.remove('hiddenCaseExplorerColumns');
  }

  getGridSetup(configParam = 'CaseSummaryStatsGrouped', selectedSolution?) {
    const userModules = [];

    this.ss.get('globals')['Modules'].map(module => {
      if (['aw', 'iw', 'li', 'pp'].includes(module.toLowerCase())) {
        userModules.push(module.toLowerCase());
      }
    });

    const defaultview = {
      filter: [
        {
          solutionId: userModules[0],
        },
        {
          savedTime: {
            selected: ['7 days'],
          },
        },
        {
          unitId: this.ss.get('globals').user.managerUnits[0].id,
        },
      ],
      sort: [],
    };

    selectedSolution =
      selectedSolution ??
      this.ss
        .get('CaseExplorerFilters')
        ?.filters.find(filter => filter['field'] === 'solutionId')?.['value'] ??
      userModules[0];

    const getHeaders = this.adminService.getUIConfigHeaders({
      uiconfig: 'rb-dash',
      type: `${selectedSolution}${configParam}`,
      implementation: 'rb',
    });
    return forkJoin([getHeaders]).pipe(
      map(([data1]) => ({ ...data1, defaultview }))
    );
  }

  getGridData(queryData, filtered = false) {
    const queryParams = JSON.parse(queryData);

    if (this['selectedUnit']) {
      queryParams['search']['unit'] = { selected: this.selectedUnit.id };
    }
    if (this['selectedUser']) {
      queryParams['search']['user'] = { selected: this.selectedUser.id };
    }
    if (filtered) {
      queryParams['skip'] = 0;
      queryParams['take'] = +this.downloadLimit.replace(',', '');
      return this.dbs.getPagedCaseSummary(queryParams, 'application/xlsx');
    }
    return this.dbs.getPagedCaseSummary(queryParams);
  }

  setHierarchy() {
    const dialogRef = this.dialog.open(HierarchyModalComponent, {
      panelClass: 'hierarchy-dialog',
      minHeight: '30vh',
      width: '50vw',
      data: { mode: 'caseExplorer' },
    });
    dialogRef.afterClosed().subscribe(result => {
      this.rbGrid['selectedUnit'] = result.data.unit || null;
      this.rbGrid['selectedUser'] = result.data.user || null;
      this.rbGrid.getGridData();
    });
  }

  clearHierarchy() {
    this.rbGrid['selectedUnit'] = null;
    this.rbGrid['selectedUser'] = null;
    this.selectedUnit = null;
    this.selectedUser = null;
    this.selectedRepCode = null;
    this.rbGrid.getGridData();
  }

  viewCase(ev, dataItem, context) {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }

    const solutions = [];

    context.ss.get('globals')?.Modules.forEach(el => {
      switch (el.toLowerCase()) {
        case 'aw':
          solutions.push({
            label: 'Annuity Wizard',
            value: el.toLowerCase(),
          });
          break;
        case 'iw':
          solutions.push({
            label: 'Investment Wizard',
            value: el.toLowerCase(),
          });
          break;
        case 'li':
          solutions.push({
            label: 'Life Insurance Wizard',
            value: el.toLowerCase(),
          });
          break;
        case 'pp':
          solutions.push({
            label: 'Product Profiler',
            value: el.toLowerCase(),
          });
          break;
        case 'rb':
          solutions.push({
            label: 'Control KB (RB)',
            value: el.toLowerCase(),
          });
          break;
        default:
          break;
      }
    });

    if (
      context.ss.get('globals')['GlobalsGroup']['UnitInfo'][
        'TurnOffSummaryPage'
      ]
    ) {
      const app = solutions[0].value;
      window.open(
        `${context.location.prepareExternalUrl('')}case/${app}/${
          dataItem.profileId
        }/results`,
        '_blank'
      );
    } else {
      window.open(
        `${context.location.prepareExternalUrl('')}case/summary/${
          dataItem.profileId
        }`,
        '_blank'
      );
    }
  }

  viewHistory(ev, dataItem, context) {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }

    const solution = context.filter.filters.find(
      filter => filter['field'] === 'solutionId'
    )['value'];

    window.open(
      `${context.location.prepareExternalUrl('')}profile-history/${solution}/${
        dataItem.profileId
      }`,
      '_blank'
    );
  }

  getCurrentSolutionFromRBGrid() {
    return this.rbGrid
      ? this.rbGrid.filter.filters.find(
          filter => filter['field'] === 'solutionId'
        )?.['value']
      : null;
  }

  dlXlsx(filtered = false) {
    let dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Disclosure',
        aiDisclosure: true,
        confirm: 'Agree and Download',
        cancel: 'Cancel/Exit',
        hideCancel: false,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        this.fileLoading = true;
        this.snacky.open(`File downloading. Please wait.`, 'Close', {
          duration: 4000,
        });
        let unit = this.unit;
        let user = null;
        const solution = this.getCurrentSolutionFromRBGrid();

        const start = this.dateSvc.toUseableString(
          this.rbGrid.filter.filters.find(
            filter =>
              filter['field'] === 'savedTime' && filter['operator'] === 'start'
          )['value'],
          'start'
        );

        const end = this.dateSvc.toUseableString(
          this.rbGrid.filter.filters.find(
            filter =>
              filter['field'] === 'savedTime' && filter['operator'] === 'end'
          )['value'],
          'end'
        );

        if (
          this.rbGrid.filter.filters.find(
            filter => filter['field'] === 'userId'
          )
        ) {
          user = this.rbGrid.filter.filters.find(
            filter => filter['field'] === 'userId'
          )?.['value'];
        }
        if (
          this.rbGrid.filter.filters.find(
            filter => filter['field'] === 'unitId'
          )
        ) {
          unit = this.rbGrid.filter.filters.find(
            filter => filter['field'] === 'unitId'
          )?.['value'];
        }

        if (this.selectedUnit) {
          unit = this.selectedUnit.id;
        }
        if (this.selectedUser) {
          user = this.selectedUser.id;
        }

        if (filtered) {
          this.getGridData(
            JSON.stringify(this.rbGrid.generateQueryData()),
            true
          ).subscribe({
            next: data => {
              this.fileLoading = false;
              let fileData;
              const reader = new FileReader();
              reader.readAsDataURL(data);
              reader.onloadend = () => {
                const date = new Date();
                const filename = `Case Explorer ${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}.xlsx`;
                const iosSafari =
                  this.pltfrm.IOS ||
                  (navigator?.['userAgentData']?.platform === 'macOS' &&
                    navigator.maxTouchPoints > 1);
                fileData = reader.result;
                saveAs(fileData, filename, {
                  proxyURL: `${this.baseUrl}/util/proxy`,
                  proxyTarget: '_self',
                  forceProxy: iosSafari,
                });
                this.fileLoading = false;
                this.snacky.open(`Download complete.`, 'Close', {
                  duration: 4000,
                });
              };
            },
            error: () => {
              this.fileLoading = false;
              this.snacky.open(
                `An unexpected error occurred while downloading this file. Please try again.`,
                'Close',
                {
                  duration: 4000,
                }
              );
            },
          });
        } else {
          this.dbs
            .getExplorerExcel(
              unit,
              solution,
              start,
              end,
              user,
              false,
              this.iteration
            )
            .subscribe(data => {
              if (data.result && data.result.length > 5) {
                const status = interval(15000).pipe(
                  mergeMap(() =>
                    this.dbs.getFileStatus(data.result).pipe(map(z => z))
                  ),
                  takeWhile(x => x.result.toLowerCase() === 'false')
                );

                status.subscribe({
                  error: err => console.error(err),
                  complete: () => this.dlXlsxFile(data.result),
                });
              }
            });
        }
      }
      dialogRef = null;
    });
  }

  dlXlsxFile(name) {
    const date = new Date();
    const filename = `Case Explorer ${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}.xlsx`;
    const iosSafari =
      this.pltfrm.IOS ||
      (navigator?.['userAgentData']?.platform === 'macOS' &&
        navigator.maxTouchPoints > 1);

    this.dbs.getXlsxFile(name).subscribe(data => {
      let fileData;
      const reader = new FileReader();
      reader.readAsDataURL(data);
      reader.onloadend = () => {
        fileData = reader.result;
        saveAs(fileData, filename, {
          proxyURL: `${this.baseUrl}/util/proxy`,
          proxyTarget: '_self',
          forceProxy: iosSafari,
        });
        this.fileLoading = false;
        this.snacky.open(`Download complete.`, 'Close', {
          duration: 4000,
        });
      };
    });
  }

  toggleNavCollapse() {
    this.navCollapsed = !this.navCollapsed;
  }
}
